/**
 * API client for standardized response format.
 * All API routes return: { success: true, data: T } | { success: false, error: string, errors?: Record<string,string> }
 */

export interface ApiError extends Error {
  status?: number;
  errors?: Record<string, string>;
}

export async function apiFetch<T = unknown>(
  url: string | URL,
  options?: RequestInit
): Promise<T> {
  const res = await fetch(url, options);
  const contentType = res.headers.get("content-type");
  const isJson = contentType?.includes("application/json");

  if (!isJson) {
    if (!res.ok) {
      throw Object.assign(new Error(`Request failed: ${res.status}`), {
        status: res.status,
      } as ApiError);
    }
    return res.text() as unknown as T;
  }

  const json = await res.json();

  if (!json.success) {
    const err = new Error(json.error || "Request failed") as ApiError;
    err.status = res.status;
    err.errors = json.errors;
    throw err;
  }

  return json.data as T;
}

/**
 * Fetch binary/blob responses (CSV, PDF, etc.).
 * Throws ApiError on failure; returns Response on success so caller can use .blob() and .headers.
 */
export async function apiFetchResponse(
  url: string | URL,
  options?: RequestInit
): Promise<Response> {
  const res = await fetch(url, options);
  if (!res.ok) {
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      const json = await res.json();
      const err = new Error(json.error || "Request failed") as ApiError;
      err.status = res.status;
      err.errors = json.errors;
      throw err;
    }
    throw Object.assign(new Error(`Request failed: ${res.status}`), {
      status: res.status,
    } as ApiError);
  }
  return res;
}
